import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { LayoutGrid, MapPin, LogOut, Search, Camera, PenLine } from "lucide-react";
import { getToken, setToken, clearToken, apiErr } from "../../lib/adminApi";

const API = process.env.REACT_APP_BACKEND_URL;

export const RequireAdmin = ({ children }) => {
  const nav = useNavigate();
  useEffect(() => {
    if (!getToken()) return;
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => { if (r.status === 401) { clearToken(); nav("/admin/login"); } })
      .catch(() => {});
  }, [nav]);
  if (!getToken()) return <Navigate to="/admin/login" replace />;
  return children;
};

export const AdminBar = ({ title }) => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-10 border-b border-white/10 pb-6">
      <div>
        <p className="overline-x mb-2">Cedar Views Admin</p>
        <h1 className="font-display tracking-tighter text-3xl sm:text-4xl text-white">{title}</h1>
      </div>
      <div className="flex items-center gap-2">
        {pathname !== "/admin" && (
          <Link to="/admin" data-testid="admin-home-link" className="px-4 py-2 text-xs font-semibold tracking-[0.15em] uppercase text-white/60 hover:text-white transition-colors">Dashboard</Link>
        )}
        <button data-testid="admin-logout-btn" onClick={() => { clearToken(); nav("/admin/login"); }}
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 hover:text-white hover:border-white text-xs font-semibold tracking-[0.15em] uppercase transition-colors">
          <LogOut size={13} /> Log out
        </button>
      </div>
    </div>
  );
};

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => { document.title = "Admin Login | Cedar Views"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(apiErr(data.detail)); return; }
      setToken(data.token);
      nav("/admin");
    } catch { setError("Could not reach the server"); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-[#050A15] px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/12 p-10">
        <p className="overline-x mb-3">Cedar Views</p>
        <h1 className="font-display tracking-tighter text-3xl text-white mb-8">Admin login.</h1>
        <label className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2">Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" autoComplete="username"
          className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm mb-6" />
        <label className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2">Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" autoComplete="current-password"
          className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm mb-8" />
        {error && <p className="text-red-400 text-xs mb-4" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="login-submit-button" className="btn-solid w-full justify-center disabled:opacity-40">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}

export function AdminDashboard() {
  useEffect(() => { document.title = "Admin | Cedar Views"; }, []);
  const cards = [
    { to: "/admin/pages", icon: LayoutGrid, t: "Product & Brand Pages", d: "Edit every section of your product pages: heroes, galleries, videos, benefits and more. Add or remove entire brand pages.", testid: "admin-card-pages" },
    { to: "/admin/areas", icon: MapPin, t: "Area Pages", d: "Add or remove local SEO area pages across Surrey, Berkshire and Hampshire.", testid: "admin-card-areas" },
    { to: "/admin/case-studies", icon: Camera, t: "Case Studies", d: "Add recent installations with photos. They appear instantly on the homepage and the Projects portfolio.", testid: "admin-card-case-studies" },
    { to: "/admin/site-content", icon: PenLine, t: "Site Content", d: "Edit everything else: contact details, menus, homepage, showrooms, contact page, testimonials, projects, privacy policy and the professionals pages.", testid: "admin-card-site-content" },
    { to: "/admin/seo", icon: Search, t: "SEO Manager", d: "Edit SEO titles, meta descriptions, H1s, canonicals and noindex per page. Manage redirects, the XML sitemap and export the full URL list.", testid: "admin-card-seo" },
  ];
  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-dashboard">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title="Dashboard." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((c) => (
            <Link key={c.to} to={c.to} data-testid={c.testid}
              className="group border border-white/12 p-10 hover:border-white/40 transition-colors block">
              <c.icon size={22} className="text-[#5B7F9E]" />
              <h2 className="font-display font-medium text-2xl text-white mt-6">{c.t}</h2>
              <p className="text-sm text-white/55 leading-relaxed mt-3">{c.d}</p>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
